import { useState, useEffect, useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { polygonDatabaseService, PolygonData, Bbox } from '@/services/indexedDB/polygonDatabase';
import { QUERY_BRAZIL_POLYGONS } from '@/graphql/queries/polygons';

type GeometryQuality = 'LOW' | 'MEDIUM' | 'HIGH';

const asQuality = (q: GeometryQuality): GeometryQuality => q;
const INITIAL_QUALITY = asQuality('LOW');
const FULL_QUALITY = asQuality('MEDIUM');

interface UsePolygonDatabaseReturn {
  isLoading: boolean;
  isReady: boolean;
  error: Error | null;
  getPolygonByCode: (municipalityCode: string) => Promise<PolygonData | undefined>;
  getPolygonsByMunicipalities: (municipalityCodes: string[]) => Promise<Map<string, PolygonData>>;
  getPolygonsInBbox: (bbox: Bbox) => Promise<PolygonData[]>;
  clearDatabase: () => Promise<void>;
}

const fetchPolygonsByQuality = async (
  apolloClient: ReturnType<typeof useApolloClient>,
  quality: GeometryQuality
) => {
  const { data } = await apolloClient.query({
    query: QUERY_BRAZIL_POLYGONS,
    variables: { quality },
  });
  return data?.brazil_polygons ?? null;
};

export const usePolygonDatabase = (): UsePolygonDatabaseReturn => {
  const apolloClient = useApolloClient();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchAndStorePolygons = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const polygons = await fetchPolygonsByQuality(apolloClient, INITIAL_QUALITY);
      if (polygons && polygons.length > 0) {
        await polygonDatabaseService.storePolygons(polygons);
        setIsReady(true);
      }

      if (INITIAL_QUALITY !== FULL_QUALITY) {
        const scheduleUpgrade = () => {
          fetchPolygonsByQuality(apolloClient, FULL_QUALITY)
            .then((fullPolygons) => {
              if (fullPolygons && fullPolygons.length > 0) {
                return polygonDatabaseService.storePolygons(fullPolygons);
              }
            })
            .catch(() => {});
        };
        if (typeof requestIdleCallback !== 'undefined') {
          requestIdleCallback(() => scheduleUpgrade(), { timeout: 3000 });
        } else {
          setTimeout(scheduleUpgrade, 2000);
        }
      }
    } catch (err) {
      if (INITIAL_QUALITY !== FULL_QUALITY) {
        try {
          const polygons = await fetchPolygonsByQuality(apolloClient, 'MEDIUM');
          if (polygons && polygons.length > 0) {
            await polygonDatabaseService.storePolygons(polygons);
            setIsReady(true);
          } else {
            setError(err instanceof Error ? err : new Error('Failed to fetch polygons'));
            setIsReady(false);
          }
        } catch {
          setError(err instanceof Error ? err : new Error('Failed to fetch polygons'));
          setIsReady(false);
        }
      } else {
        setError(err instanceof Error ? err : new Error('Failed to fetch polygons'));
        setIsReady(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [apolloClient]);

  const initializeDatabase = useCallback(async () => {
    try {
      setIsLoading(true);
      const dataExists = await polygonDatabaseService.checkDataExists();

      if (dataExists) {
        setIsReady(true);
        setIsLoading(false);
      } else {
        await fetchAndStorePolygons();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Failed to initialize database');
      setError(errorMessage);
      setIsReady(false);
      setIsLoading(false);
    }
  }, [fetchAndStorePolygons]);

  useEffect(() => {
    initializeDatabase();
  }, [initializeDatabase]);

  const getPolygonByCode = useCallback(
    async (municipalityCode: string): Promise<PolygonData | undefined> => {
      if (!isReady) {
        return undefined;
      }

      try {
        return await polygonDatabaseService.getPolygonByCode(municipalityCode);
      } catch (err) {
        return undefined;
      }
    },
    [isReady]
  );

  const getPolygonsByMunicipalities = useCallback(
    async (municipalityCodes: string[]): Promise<Map<string, PolygonData>> => {
      if (!isReady) {
        return new Map<string, PolygonData>();
      }

      try {
        return await polygonDatabaseService.getPolygonsByCodes(municipalityCodes);
      } catch (err) {
        return new Map<string, PolygonData>();
      }
    },
    [isReady]
  );

  const getPolygonsInBbox = useCallback(
    async (bbox: Bbox): Promise<PolygonData[]> => {
      if (!isReady) {
        return [];
      }

      try {
        return await polygonDatabaseService.getPolygonsInBbox(bbox);
      } catch {
        return [];
      }
    },
    [isReady]
  );

  const clearDatabase = useCallback(async () => {
    try {
      await polygonDatabaseService.clearDatabase();
      setIsReady(false);
      await fetchAndStorePolygons();
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Failed to clear database');
      setError(errorMessage);
    }
  }, [fetchAndStorePolygons]);

  return {
    isLoading,
    isReady,
    error,
    getPolygonByCode,
    getPolygonsByMunicipalities,
    getPolygonsInBbox,
    clearDatabase,
  };
};
